import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const ReportHistoryModal = ({
  show,
  onClose,
  recipientCode,
  donorCode,
  recipientClinicals,
  donorClinicals,
}) => {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (show && recipientCode && donorCode) {
      const historyKey = `aiAnalysisHistory_${recipientCode}_${donorCode}`;
      const storedHistory = JSON.parse(localStorage.getItem(historyKey)) || [];
      setHistory(storedHistory);
    }
  }, [show, recipientCode, donorCode]);

  if (!show) return null;

  const deleteReport = (index) => {
    const updatedHistory = history.filter((_, i) => i !== index);
    setHistory(updatedHistory);
    const historyKey = `aiAnalysisHistory_${recipientCode}_${donorCode}`;
    localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
  };

  const downloadReportAsPDF = (report, timestamp) => {
    const doc = new jsPDF();
    const date = new Date(timestamp).toLocaleString();
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    const lineHeight = 6;

    // Header section
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("DonoSure AI Generated Report", margin, 25);

    // Add a line under the title
    doc.setLineWidth(0.5);
    doc.line(margin, 30, pageWidth - margin, 30);

    // Date and patient info
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on: ${date}`, margin, 40);
    doc.text(`Recipient: ${recipientCode} | Donor: ${donorCode}`, margin, 48);

    let currentY = 60;

    // Add HLA typing table if clinical data is available
    if (recipientClinicals && donorClinicals) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("HLA Typing Comparison", margin, currentY);
      currentY += 10;

      const getHLAValue = (clinicals, allele) =>
        clinicals?.hlaTyping?.[allele] || "N/A";

      const hlaData = [
        ["Locus", "Recipient", "Donor"],
        [
          "A",
          `${getHLAValue(recipientClinicals, "HLA_A_Allele_1")}, ${getHLAValue(
            recipientClinicals,
            "HLA_A_Allele_2"
          )}`,
          `${getHLAValue(donorClinicals, "HLA_A_Allele_1")}, ${getHLAValue(
            donorClinicals,
            "HLA_A_Allele_2"
          )}`,
        ],
        [
          "B",
          `${getHLAValue(recipientClinicals, "HLA_B_Allele_1")}, ${getHLAValue(
            recipientClinicals,
            "HLA_B_Allele_2"
          )}`,
          `${getHLAValue(donorClinicals, "HLA_B_Allele_1")}, ${getHLAValue(
            donorClinicals,
            "HLA_B_Allele_2"
          )}`,
        ],
        [
          "C",
          `${getHLAValue(recipientClinicals, "HLA_C_Allele_1")}, ${getHLAValue(
            recipientClinicals,
            "HLA_C_Allele_2"
          )}`,
          `${getHLAValue(donorClinicals, "HLA_C_Allele_1")}, ${getHLAValue(
            donorClinicals,
            "HLA_C_Allele_2"
          )}`,
        ],
        [
          "DRB1",
          `${getHLAValue(
            recipientClinicals,
            "HLA_DRB1_Allele_1"
          )}, ${getHLAValue(recipientClinicals, "HLA_DRB1_Allele_2")}`,
          `${getHLAValue(donorClinicals, "HLA_DRB1_Allele_1")}, ${getHLAValue(
            donorClinicals,
            "HLA_DRB1_Allele_2"
          )}`,
        ],
        [
          "DQB1",
          `${getHLAValue(
            recipientClinicals,
            "HLA_DQB1_Allele_1"
          )}, ${getHLAValue(recipientClinicals, "HLA_DQB1_Allele_2")}`,
          `${getHLAValue(donorClinicals, "HLA_DQB1_Allele_1")}, ${getHLAValue(
            donorClinicals,
            "HLA_DQB1_Allele_2"
          )}`,
        ],
        [
          "DPB1",
          `${getHLAValue(
            recipientClinicals,
            "HLA_DPB1_Allele_1"
          )}, ${getHLAValue(recipientClinicals, "HLA_DPB1_Allele_2")}`,
          `${getHLAValue(donorClinicals, "HLA_DPB1_Allele_1")}, ${getHLAValue(
            donorClinicals,
            "HLA_DPB1_Allele_2"
          )}`,
        ],
      ];

      doc.autoTable({
        startY: currentY,
        head: [hlaData[0]],
        body: hlaData.slice(1),
        theme: "striped",
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 10,
          fontStyle: "bold",
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 3,
        },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        columnStyles: {
          0: { cellWidth: 30, fontStyle: "bold" },
          1: { cellWidth: 75 },
          2: { cellWidth: 75 },
        },
        margin: { left: margin, right: margin },
      });

      currentY = doc.lastAutoTable.finalY + 15;
    }

    // Clean markdown to text conversion
    const cleanMarkdownToText = (markdown) => {
      let text = markdown;

      // Remove markdown syntax but preserve structure
      text = text
        // Remove headers but keep the text
        .replace(/^#{1,6}\s+(.+)$/gm, "$1")
        // Remove bold/italic formatting
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/__(.*?)__/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/_(.*?)_/g, "$1")
        // Clean up tables - convert to readable format
        .replace(/\|(.+)\|/g, (match, content) => {
          return content
            .split("|")
            .map((cell) => cell.trim())
            .filter((cell) => cell && !cell.match(/^[-\s:]+$/))
            .join(" | ");
        })
        // Remove table separators
        .replace(/^\|[-\s:]+\|$/gm, "")
        // Convert lists
        .replace(/^[\s]*[-*+]\s+(.+)$/gm, "• $1")
        .replace(/^[\s]*(\d+)\.\s+(.+)$/gm, "$1. $2")
        // Clean up extra whitespace
        .replace(/\n\s*\n\s*\n/g, "\n\n")
        .replace(/^\s+|\s+$/g, "");

      return text;
    };

    // Add AI Analysis section header
    if (currentY + 20 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("AI Analysis Report", margin, currentY);
    currentY += 15;

    // Process the cleaned report
    const cleanedReport = cleanMarkdownToText(report);

    // Split into paragraphs and process each
    const paragraphs = cleanedReport.split(/\n\s*\n/);

    paragraphs.forEach((paragraph) => {
      if (!paragraph.trim()) return;

      // Check if this is a header-like line (short, uppercase, etc.)
      const isHeader =
        paragraph.length < 100 &&
        (paragraph.toUpperCase() === paragraph ||
          paragraph.match(/^[A-Z][A-Z\s&\-:]+$/));

      if (isHeader) {
        // Add space before headers
        currentY += 8;

        // Check if we need a new page
        if (currentY + 15 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);

        const headerLines = doc.splitTextToSize(paragraph, maxWidth);
        headerLines.forEach((line) => {
          doc.text(line, margin, currentY);
          currentY += lineHeight + 1;
        });

        currentY += 5;
      } else {
        // Regular paragraph
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        // Check if we need a new page
        const estimatedLines = Math.ceil(paragraph.length / 80);
        if (currentY + estimatedLines * lineHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }

        const lines = doc.splitTextToSize(paragraph, maxWidth);
        lines.forEach((line) => {
          doc.text(line, margin, currentY);
          currentY += lineHeight;
        });

        currentY += 4; // Space between paragraphs
      }
    });

    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth - margin - 20,
        pageHeight - 10
      );
      doc.text("Generated by DonoSure AI", margin, pageHeight - 10);
    }

    const filename = `AI_Analysis_Report_${recipientCode}_${donorCode}_${date.replace(
      /[/:\\]/g,
      "_"
    )}.pdf`;
    doc.save(filename);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto bg-black bg-opacity-50 outline-none focus:outline-none">
      <div className="relative w-full max-w-4xl mx-auto my-6">
        <div className="relative flex flex-col w-full bg-white border-0 rounded-lg shadow-lg outline-none focus:outline-none">
          <div className="flex items-start justify-between p-5 bg-gray-100 border-b border-solid rounded-t border-blueGray-200">
            <h3 className="text-2xl font-semibold text-gray-800">
              Report History for {recipientCode} - {donorCode}
            </h3>
            <button
              className="p-1 ml-auto text-3xl font-semibold leading-none text-gray-600 bg-transparent border-0 outline-none opacity-70 hover:opacity-100 focus:outline-none"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <div className="relative flex-auto p-6 overflow-y-auto max-h-[70vh]">
            {history.length === 0 ? (
              <p className="text-gray-600">No history available.</p>
            ) : (
              [...history].reverse().map((item, index) => (
                <div
                  key={index}
                  className="p-4 mb-8 transition-shadow duration-200 border border-gray-200 rounded-lg shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-gray-600">
                      Generated on: {new Date(item.timestamp).toLocaleString()}
                    </p>
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          console.log("Downloading report:", item);
                          downloadReportAsPDF(item.report, item.timestamp);
                        }}
                        className="flex items-center px-3 py-1 mr-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                        aria-label="Download PDF"
                        title="Download PDF"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                            clipRule="evenodd"
                          />
                        </svg>
                        PDF
                      </button>
                      <button
                        onClick={() => deleteReport(history.length - 1 - index)}
                        className="flex items-center px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                        aria-label="Delete report"
                        title="Delete report"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"
                            clipRule="evenodd"
                          />
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                  <ReactMarkdown
                    className="prose prose-sm max-w-none"
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({ node, ...props }) => (
                        <div className="overflow-x-auto my-4">
                          <table
                            {...props}
                            className="min-w-full border-collapse border border-gray-300 bg-white text-sm"
                          />
                        </div>
                      ),
                      th: ({ node, ...props }) => (
                        <th
                          {...props}
                          className="border border-gray-300 bg-gray-100 px-3 py-2 text-left font-semibold text-gray-900"
                        />
                      ),
                      td: ({ node, ...props }) => (
                        <td
                          {...props}
                          className="border border-gray-300 px-3 py-2 text-gray-800"
                        />
                      ),
                      h1: ({ node, ...props }) => (
                        <h1
                          {...props}
                          className="text-2xl font-bold text-gray-900 border-b-2 border-blue-500 pb-2 mb-3"
                        />
                      ),
                      h2: ({ node, ...props }) => (
                        <h2
                          {...props}
                          className="text-xl font-semibold text-gray-800 mt-4 mb-2 border-l-4 border-blue-500 pl-2"
                        />
                      ),
                      h3: ({ node, ...props }) => (
                        <h3
                          {...props}
                          className="text-lg font-medium text-gray-700 mt-3 mb-2"
                        />
                      ),
                      strong: ({ node, ...props }) => (
                        <strong
                          {...props}
                          className="font-semibold text-gray-900"
                        />
                      ),
                      p: ({ node, ...props }) => (
                        <p
                          {...props}
                          className="mb-3 text-gray-800 leading-relaxed"
                        />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          className="text-blue-600 hover:text-blue-800 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  >
                    {item.report}
                  </ReactMarkdown>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-end p-6 border-t border-solid rounded-b border-blueGray-200 bg-gray-50">
            <button
              className="px-6 py-2 text-sm font-bold text-white uppercase transition-all duration-150 ease-linear bg-red-500 rounded shadow outline-none hover:bg-red-600 hover:shadow-lg focus:outline-none"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportHistoryModal;
